import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useLanguage } from '../context/LanguageContext';
import { ChartLineUp } from '@phosphor-icons/react';
import InfoHint from './common/InfoHint';

/**
 * CAPAN declared-capacity regression — glass-box panel (AP-CAM Rev 5.2, task A).
 *
 * Renders the empirical occupancy-vs-movements scatter (from D-FOL 60-min windows),
 * the fitted curve, the CWL ceiling line, and the crossing = declared controller
 * capacity. It is the empirical counterpart to SC_controller = 60·Phi/W; the gap
 * from the controller ceiling down to RAC is the CF complexity reduction.
 *
 * Reads analysis block6.capacity_regression. Dormant (renders null) until D-FOL
 * occupancy samples are supplied to the pipeline (#24 dfol_ingest / direct upload).
 */

const C_BLUE = '#3A7CA5';
const C_ACCENT = '#F26430';
const C_GRID = '#20497B';
const C_TEXT = '#A4B8D3';
const C_CEIL = '#E0B000';

interface CapReg {
  ok: boolean;
  model?: string;
  coeffs?: { a1?: number; a2?: number };
  r2?: number;
  w_per_op_min?: number | null;
  declared_capacity_mov_h?: number | null;
  cwl_target?: number;
  ceiling_min?: number;
  n_points?: number;
  mov_range?: [number, number];
  saturated_sample?: boolean;
  points?: [number, number][];
}

interface Props {
  data?: CapReg | null;
  scController?: number | null;
  rac?: number | null;
  isDark?: boolean;
}

export default function CapacityRegressionPanel({ data, scController, rac }: Props) {
  const { language } = useLanguage();
  const es = language === 'es';

  if (!data || !data.ok || !data.points?.length) return null; // dormant until D-FOL samples flow (#24)

  const ceiling = data.ceiling_min ?? 42;
  const cap = data.declared_capacity_mov_h ?? 0;
  const movMax = Math.max(cap * 1.1, data.mov_range?.[1] ?? cap, 1);
  const a1 = data.coeffs?.a1 ?? 0;
  const a2 = data.coeffs?.a2 ?? 0;

  const fit: [number, number][] = [];
  for (let i = 0; i <= 40; i++) {
    const m = (movMax * i) / 40;
    fit.push([m, a2 * m * m + a1 * m]);
  }

  const options: Highcharts.Options = {
    chart: { backgroundColor: 'transparent', height: 300, spacing: [8, 8, 8, 8] },
    title: { text: undefined },
    credits: { enabled: false },
    legend: { itemStyle: { color: C_TEXT, fontSize: '11px' } },
    xAxis: {
      title: { text: es ? 'Movimientos / hora' : 'Movements / hour', style: { color: C_TEXT } },
      gridLineColor: C_GRID, lineColor: C_GRID, tickColor: C_GRID,
      labels: { style: { color: C_TEXT } },
      min: 0, max: movMax,
      plotLines: cap ? [{
        value: cap, color: C_ACCENT, dashStyle: 'Dash', width: 2, zIndex: 4,
        label: { text: `${es ? 'Cap. declarada' : 'Declared cap.'} ${cap}`, style: { color: C_ACCENT, fontSize: '10px' } },
      }] : [],
    },
    yAxis: {
      title: { text: es ? 'Ocupación activa (min/h)' : 'Active occupancy (min/h)', style: { color: C_TEXT } },
      gridLineColor: C_GRID,
      labels: { style: { color: C_TEXT } },
      min: 0,
      plotLines: [{
        value: ceiling, color: C_CEIL, dashStyle: 'Dash', width: 2, zIndex: 4,
        label: { text: `${es ? 'Techo' : 'Ceiling'} CWL ${Math.round((data.cwl_target ?? 0.7) * 100)}% = ${ceiling} min`, style: { color: C_CEIL, fontSize: '10px' } },
      }],
    },
    tooltip: {
      backgroundColor: '#0e1b33', borderColor: C_GRID, style: { color: C_TEXT },
      formatter() { return `${(this.x as number)?.toFixed(0)} mov/h → ${(this.y as number)?.toFixed(1)} min`; },
    },
    series: [
      {
        type: 'scatter', name: es ? 'D-FOL (ventanas 60 min)' : 'D-FOL (60-min windows)',
        color: C_BLUE, data: data.points, marker: { radius: 3, symbol: 'circle' },
      },
      {
        type: 'line', name: es ? `Ajuste ${data.model} (R²=${data.r2})` : `Fit ${data.model} (R²=${data.r2})`,
        color: C_ACCENT, data: fit, marker: { enabled: false }, enableMouseTracking: false, dashStyle: 'ShortDot',
      },
    ],
  };

  return (
    <div className="p-4 rounded-lg border bg-[var(--surface-raised)] border-[var(--border)]">
      <div className="flex items-center gap-2 mb-3">
        <ChartLineUp size={18} className="text-[#F26430]" weight="bold" />
        <h4 className="text-sm font-bold uppercase text-[var(--text-primary)]">
          {es ? 'Curva de capacidad declarada (CAPAN)' : 'Declared-capacity curve (CAPAN)'}
        </h4>
        <InfoHint text={es
          ? 'Relación empírica movimientos↔ocupación activa del controlador (ventanas de 60 min del D-FOL). El cruce con el techo CWL da la capacidad declarada del controlador — contraparte empírica de SC=60·Φ/W. El hueco hasta el RAC es la reducción por complejidad (CF).'
          : 'Empirical movements↔active-occupancy relation (D-FOL 60-min windows). The crossing with the CWL ceiling gives the declared controller capacity — empirical counterpart of SC=60·Φ/W. The gap down to RAC is the complexity reduction (CF).'} />
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
        <Stat label={es ? 'Cap. declarada' : 'Declared cap.'} value={`${cap} mov/h`} accent />
        <Stat label="R²" value={`${data.r2}`} />
        <Stat label={es ? 'W/op (medido)' : 'W/op (measured)'} value={data.w_per_op_min ? `${(data.w_per_op_min * 60).toFixed(1)} s` : '—'} />
        <Stat label={es ? 'Ventanas' : 'Windows'} value={`${data.n_points}`} />
        {scController != null && <Stat label="SC_controller" value={`${scController.toFixed(0)} mov/h`} />}
        {rac != null && <Stat label="RAC" value={`${rac.toFixed(0)} mov/h`} accent />}
      </div>

      {data.saturated_sample === false && (
        <p className="text-xs text-[var(--aipron-warning)] mt-2">
          {es
            ? '⚠ La demanda observada no alcanza el techo: la capacidad declarada es una EXTRAPOLACIÓN (no se observó la rodilla de congestión). Para la rodilla real se necesitan datos saturados o la FTS.'
            : '⚠ Observed demand does not reach the ceiling: the declared capacity is an EXTRAPOLATION (no congestion knee observed). The real knee needs saturated data or the FTS.'}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="p-2 rounded bg-[var(--surface)] border border-[var(--border)]/40">
      <div className="text-[var(--text-tertiary)]">{label}</div>
      <div className={`font-bold ${accent ? 'text-[#F26430]' : 'text-[var(--text-primary)]'}`}>{value}</div>
    </div>
  );
}
